import { useCallback, useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SofaIcon,
  BedIcon,
  UtensilsIcon,
  BathIcon,
  ShirtIcon,
  AirVentIcon,
  DoorOpenIcon,
  LayoutGridIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  AlertCircleIcon,
  InfoIcon
} from 'lucide-react';
// Components
import { LoginScreen } from '../components/LoginScreen';
import { UnitSelectionScreen } from '../components/UnitSelectionScreen';
import { MobileHeader } from './components/MobileHeader';
import { BottomNavigation, MainTabType } from './components/BottomNavigation';
import { MenuSheet } from './components/MenuSheet';
import { UserManagementModal } from './components/UserManagementModal';
import { SensorListView } from './components/SensorListView';
import { AlarmsSheet } from './components/AlarmsSheet';
import { SensorDetailSidebar } from './components/SensorDetailSidebar';
import { AllSpacesView } from './components/AllSpacesView';
import { Sensor, FloorPlanHeatMap } from './components/FloorPlanHeatMap';
import { CurtainController } from '../components/CurtainController';
// Services & Hooks
import { thingsboardService } from '../services/thingsboard';
import { useEditorZones } from '../hooks/useEditorZones';
import { useAlarms } from '../hooks/useAlarms';
import { useZonesTelemetry } from '../hooks/useZonesTelemetry';
import { ViewMode, VIEW_MODES } from '../utils/viewModeUtils';
import { useTranslation } from 'react-i18next';

// ─── Constants ───
const ROOM_ICON_MAP: Record<string, any> = {
  '거실': SofaIcon,
  '침실': BedIcon,
  '침실1': BedIcon,
  '침실2': BedIcon,
  '안방': BedIcon,
  '주방': UtensilsIcon,
  '화장실': BathIcon,
  '화장실1': BathIcon,
  '화장실2': BathIcon,
  '드레스룸': ShirtIcon,
  '실외기실': AirVentIcon,
  '현관': DoorOpenIcon,
  '알파룸': LayoutGridIcon,
  '사무실': LayoutGridIcon,
  '회의실': LayoutGridIcon,
  '로비': DoorOpenIcon,
  '휴게실': SofaIcon,
  '탕비실': UtensilsIcon,
  '서버실': AirVentIcon,
  '측정실': LayoutGridIcon,
  '측정실현관': DoorOpenIcon,
};

const DEFAULT_ICON = LayoutGridIcon;

import { SceneListView } from './components/SceneListView';
import { SceneEditorModal } from './components/SceneEditorModal';
import { sceneService } from './services/sceneService';

// ─── View Types ───
type AppView = 'login' | 'unitSelect' | 'main' | 'sensorDetail' | 'floorPlan' | 'scenes';
type SlideDirection = 'left' | 'right' | 'up' | 'down' | null;

const slideVariants = {
  enterFromRight: {
    x: '100%',
    opacity: 0
  },
  enterFromLeft: {
    x: '-100%',
    opacity: 0
  },
  center: {
    x: 0,
    opacity: 1
  },
  exitToRight: {
    x: '100%',
    opacity: 0
  },
  exitToLeft: {
    x: '-100%',
    opacity: 0
  }
};

export function B2BMobileApp() {
  const { t } = useTranslation();

  // ─── Theme Management ───
  type ThemePreference = 'dark' | 'light' | 'system';
  const [themePref, setThemePref] = useState<ThemePreference>(() => {
    return (localStorage.getItem('mobileThemePref') as ThemePreference) || 'system';
  });
  const [systemIsDark, setSystemIsDark] = useState<boolean>(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedTheme: 'dark' | 'light' = useMemo(() => {
    if (themePref === 'system') return systemIsDark ? 'dark' : 'light';
    return themePref;
  }, [themePref, systemIsDark]);

  const toggleTheme = useCallback(() => {
    setThemePref((prev) => {
      const next = prev === 'system' ? 'light' : prev === 'light' ? 'dark' : 'system';
      localStorage.setItem('mobileThemePref', next);
      return next;
    });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  // Auth & Space
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('jwtToken'));
  const [apartmentAssetId, setApartmentAssetId] = useState<string | null>(localStorage.getItem('apartmentAssetId'));
  const [apartmentLabel, setApartmentLabel] = useState<string | null>(localStorage.getItem('apartmentLabel'));

  // Check if accessed via QR / SignUp invitation link
  const isQrOrSignUpUrl = useMemo(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      return (
        searchParams.has('customer') ||
        searchParams.has('customerCode') ||
        searchParams.has('code') ||
        searchParams.has('customerId') ||
        searchParams.has('signup')
      );
    } catch {
      return false;
    }
  }, []);

  const [forceSignUpMode, setForceSignUpMode] = useState(isQrOrSignUpUrl);

  // Navigation
  const [currentView, setCurrentView] = useState<AppView>(() => {
    const hasToken = !!localStorage.getItem('jwtToken');
    const hasAsset = !!localStorage.getItem('apartmentAssetId');

    // If accessed via QR link, start in login/signup
    try {
      const searchParams = new URLSearchParams(window.location.search);
      if (
        searchParams.has('customer') ||
        searchParams.has('customerCode') ||
        searchParams.has('code') ||
        searchParams.has('customerId') ||
        searchParams.has('signup')
      ) {
        return 'login';
      }
    } catch {}

    // Restore session seamlessly on page refresh
    if (hasToken) {
      return hasAsset ? 'main' : 'unitSelect';
    }
    return 'login';
  });
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('left');
  const [mainTab, setMainTab] = useState<MainTabType>('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // UI State
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>('ALL');
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  const [editingScene, setEditingScene] = useState<any | null>(null);
  const [showAlarms, setShowAlarms] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('energy');
  const [floorPlanUrl, setFloorPlanUrl] = useState<string | null>(null);
  const [deviceToast, setDeviceToast] = useState<{ name: string; alias?: string } | null>(null);
  const [appToast, setAppToast] = useState<{ message: string; type: 'success' | 'info' | 'error' } | null>(null);
  // Keep track of local label overrides when updated in real-time
  const [sensorLabelOverrides, setSensorLabelOverrides] = useState<Record<string, string>>({});

  // Real-time Data Hooks
  const { messages: alarmMessages } = useAlarms(apartmentAssetId);
  const { zones: allZones } = useEditorZones(apartmentAssetId);

  // User RBAC Permissions & State
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [userRole, setUserRole] = useState<'CUSTOMER_ADMIN' | 'ZONE_USER'>('ZONE_USER');
  const [allowedZoneIds, setAllowedZoneIds] = useState<string[] | null>(null);
  const [canControl, setCanControl] = useState<boolean>(true);
  const [isUserMgmtOpen, setIsUserMgmtOpen] = useState(false);
  const [currentSpaceCustomerId, setCurrentSpaceCustomerId] = useState<string>('');

  // Fetch current apartment/space Customer ID
  useEffect(() => {
    async function loadSpaceCustomerId() {
      if (!apartmentAssetId) return;
      try {
        const asset = await thingsboardService.getAsset(apartmentAssetId);
        if (asset?.customerId?.id && asset.customerId.id !== '13814000-1dd2-11b2-8080-808080808080') {
          setCurrentSpaceCustomerId(asset.customerId.id);
        } else if (currentUser?.customerId?.id && currentUser.customerId.id !== '13814000-1dd2-11b2-8080-808080808080') {
          setCurrentSpaceCustomerId(currentUser.customerId.id);
        }
      } catch (err) {
        console.error('Failed to load space customer ID', err);
      }
    }
    loadSpaceCustomerId();
  }, [apartmentAssetId, currentUser]);

  const isTenantAdmin = currentUser?.authority === 'TENANT_ADMIN' || currentUser?.authority === 'SYS_ADMIN';
  const isCustomerAdmin = userRole === 'CUSTOMER_ADMIN' || currentUser?.additionalInfo?.userRole === 'CUSTOMER_ADMIN';
  const isAdmin = isTenantAdmin || isCustomerAdmin;

  // Load User Profile and RBAC permissions
  useEffect(() => {
    async function loadUserProfileAndPermissions() {
      if (!authToken) return;
      try {
        const user = await thingsboardService.getUser();
        console.log('Current logged in user profile:', user);
        setCurrentUser(user);

        if (user?.authority === 'TENANT_ADMIN' || user?.authority === 'SYS_ADMIN') {
          setUserRole('CUSTOMER_ADMIN');
          setAllowedZoneIds(null);
          setCanControl(true);
          return;
        }

        const additionalInfo = user?.additionalInfo || {};
        // Default customer users to ZONE_USER unless explicitly CUSTOMER_ADMIN
        let role: 'CUSTOMER_ADMIN' | 'ZONE_USER' = (additionalInfo.userRole === 'CUSTOMER_ADMIN') ? 'CUSTOMER_ADMIN' : 'ZONE_USER';
        let allowed = additionalInfo.allowedZoneIds || [];
        let ctrl = additionalInfo.canControl !== undefined ? additionalInfo.canControl : true;

        if (user?.id?.id) {
          try {
            const attrs = await thingsboardService.getUserServerAttributes(user.id.id);
            if (attrs.userRole) role = attrs.userRole === 'CUSTOMER_ADMIN' ? 'CUSTOMER_ADMIN' : 'ZONE_USER';
            if (attrs.allowedZoneIds) allowed = attrs.allowedZoneIds;
            if (attrs.canControl !== undefined) ctrl = attrs.canControl;
          } catch {
            // Ignore
          }
        }

        console.log('Final resolved user role & allowed zones:', role, allowed);
        setUserRole(role);
        setCanControl(ctrl);

        if (role === 'ZONE_USER') {
          try {
            const parsed = typeof allowed === 'string' ? JSON.parse(allowed) : allowed;
            setAllowedZoneIds(Array.isArray(parsed) ? parsed : []);
          } catch {
            setAllowedZoneIds([]);
          }
        } else {
          setAllowedZoneIds(null);
        }
      } catch (err) {
        console.error('Failed to load user profile/permissions', err);
      }
    }
    loadUserProfileAndPermissions();
  }, [authToken]);

  // Filtered zones based on permission
  const zones = useMemo(() => {
    if (userRole === 'CUSTOMER_ADMIN' || isTenantAdmin) {
      return allZones;
    }
    // For regular ZONE_USER, only return zones in allowedZoneIds
    if (!allowedZoneIds || allowedZoneIds.length === 0) {
      return [];
    }
    return allZones.filter(z => allowedZoneIds.includes(z.id));
  }, [allZones, allowedZoneIds, userRole, isTenantAdmin]);

  // Keep selected room valid within allowed zones
  useEffect(() => {
    if (userRole === 'ZONE_USER') {
      if (zones.length > 0) {
        if (!selectedRoomId || selectedRoomId === 'ALL' || !zones.some(z => z.id === selectedRoomId)) {
          setSelectedRoomId(zones[0].id);
        }
      }
    }
  }, [zones, userRole, selectedRoomId]);

  const zoneIds = useMemo(() => zones.map(z => z.id), [zones]);
  const { zonesData: assetTelemetry } = useZonesTelemetry(
    zoneIds,
    ['temperature', 'zone_total_power', 'calculated_24h_power', 'humidity', 'CO2', 'TVOCs', 'dust', 'presence_state', 'light_state']
  );

  // Flatten all sensors
  const allSensors = useMemo(() => zones.flatMap(z => (z.sensors || []).map(s => ({
    ...s,
    label: sensorLabelOverrides[s.id] || s.label,
    roomId: z.id,
    roomName: z.name
  }))), [zones, sensorLabelOverrides]);

  // Current selected room sensors
  const currentZone = zones.find(z => z.id === selectedRoomId) || zones[0];
  const currentSensors = currentZone?.sensors || [];

  // Sensor telemetry subscription - Subscribe to ALL sensors across all spaces
  const sensorIds = useMemo(() => allSensors.map(s => s.id), [allSensors]);
  const { zonesData: sensorTelemetry } = useZonesTelemetry(
    sensorIds,
    [
      'temperature',
      'humidity',
      'power_instant',
      'CO2',
      'TVOCs',
      'dust',
      'presence_state',
      'light_state',
      'curtain_state',
      'switch',
      'plug_on',
      'plug_mode',
      'state',
      'p1',
      'p2',
      'p3',
      'erv',
      'ERV'
    ],
    'DEVICE'
  );

  // All SEO / Relay sensors for whole space
  const seoSensors = useMemo(() => allSensors.filter(s => s.type === 'SEO' || s.type === 'SEM' || s.type === 'SER' || s.type === 'PLUG' || s.type === 'OUTLET'), [allSensors]);

  // Listen for device events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setDeviceToast({ name: detail.name, alias: detail.alias });
    };
    window.addEventListener('device-toast', handler);
    return () => window.removeEventListener('device-toast', handler);
  }, []);

  // Fetch floorplan URL
  useEffect(() => {
    async function fetchApartmentData() {
      if (!apartmentAssetId || apartmentAssetId === '00000000-0000-0000-0000-000000000000') {
        setFloorPlanUrl(null);
        return;
      }
      try {
        const attrs = await thingsboardService.getEntityAttributes('ASSET', apartmentAssetId, 'SERVER_SCOPE', ['floorplan', 'homeName']);
        if (attrs && attrs.length > 0) {
          const fpAttr = attrs.find((a: any) => a.key === 'floorplan');
          if (fpAttr && fpAttr.value && typeof fpAttr.value === 'string' && fpAttr.value.trim() !== '') {
            const val = fpAttr.value.trim();
            setFloorPlanUrl(val.startsWith('http') ? val : `https://iot.seqsys.net${val}`);
          } else {
            setFloorPlanUrl(null);
          }
        } else {
          setFloorPlanUrl(null);
        }
      } catch (err) {
        console.error('Failed to fetch floorplan attributes:', err);
        setFloorPlanUrl(null);
      }
    }
    fetchApartmentData();
  }, [apartmentAssetId]);

  useEffect(() => {
    if (zones.length > 0 && (!selectedRoomId || selectedRoomId === 'ALL')) {
      // If user has restricted permissions, default to the first accessible zone
      if (allowedZoneIds && allowedZoneIds.length > 0 && selectedRoomId === 'ALL') {
        setSelectedRoomId(zones[0].id);
      }
    }
  }, [zones, selectedRoomId, allowedZoneIds]);

  // Toast handler
  const handleShowToast = useCallback((message: string, type: 'success' | 'info' | 'error' = 'success') => {
    setAppToast({ message, type });
    setTimeout(() => {
      setAppToast(null);
    }, 3000);
  }, []);

  // ─── Login & Asset Selection Flow ───
  const handleLogin = (jwtToken: string) => {
    // 1. Reset previous space and user state to prevent permission leakage across accounts
    setApartmentAssetId(null);
    setApartmentLabel(null);
    setCurrentUser(null);
    setUserRole('ZONE_USER');
    setAllowedZoneIds(null);
    setSelectedRoomId('ALL');
    setCurrentSpaceCustomerId('');
    setSensorLabelOverrides({});
    localStorage.removeItem('apartmentAssetId');
    localStorage.removeItem('apartmentLabel');

    // 2. Set new auth token and proceed to unit select
    setAuthToken(jwtToken);
    localStorage.setItem('jwtToken', jwtToken);
    setForceSignUpMode(false);
    try {
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch {}
    setSlideDirection('left');
    setCurrentView('unitSelect');
  };

  const handleUnitSelect = (assetId: string, assetName: string) => {
    setApartmentAssetId(assetId);
    setApartmentLabel(assetName);
    localStorage.setItem('apartmentAssetId', assetId);
    localStorage.setItem('apartmentLabel', assetName);
    setSlideDirection('left');
    setCurrentView('main');
  };

  const handleLogout = () => {
    thingsboardService.logout();
    setAuthToken(null);
    setApartmentAssetId(null);
    setApartmentLabel(null);
    setCurrentUser(null);
    setUserRole('ZONE_USER');
    setAllowedZoneIds(null);
    setSelectedRoomId('');
    setCurrentSpaceCustomerId('');
    setCurrentView('login');
    // Hard refresh to ensure all websocket subscriptions and cached memory are completely cleaned
    setTimeout(() => {
      window.location.href = window.location.origin + window.location.pathname;
    }, 100);
  };

  const handleBackToUnits = () => {
    setSlideDirection('right');
    setCurrentView('unitSelect');
  };

  const ROOMS = useMemo(() => {
    const baseRooms = zones.map(z => {
      const data = assetTelemetry[z.id] || {};
      const temp = Number(data.temperature?.[0]?.value || 0);
      const humidity = Number(data.humidity?.[0]?.value || 0);
      const energy = Number(data.calculated_24h_power?.[0]?.value || 0);
      const co2 = Number(data.CO2?.[0]?.value || 0);
      const tvoc = Number(data.TVOCs?.[0]?.value || 0);
      const dust = Number(data.dust?.[0]?.value || 0);

      const aqi = Math.round((
        (co2 < 800 ? 100 : co2 < 1200 ? 70 : 40) +
        (tvoc < 0.3 ? 100 : tvoc < 0.6 ? 70 : 40) +
        (dust < 15 ? 100 : dust < 35 ? 70 : 40)
      ) / 3);

      return {
        id: z.id,
        name: z.name,
        sensors: (z.sensors || []).map(s => ({ ...s, roomId: z.id, roomName: z.name })),
        temp,
        humidity,
        energy,
        aqi,
        icon: ROOM_ICON_MAP[z.name] || DEFAULT_ICON,
      };
    });

    // Only add 'ALL' if user is ADMIN and has multiple zones
    if ((userRole === 'CUSTOMER_ADMIN' || isTenantAdmin) && baseRooms.length > 1) {
      const allRoom = {
        id: 'ALL',
        name: '전체 공간',
        temp: baseRooms.length ? baseRooms.reduce((acc, r) => acc + r.temp, 0) / baseRooms.length : 0,
        humidity: baseRooms.length ? baseRooms.reduce((acc, r) => acc + r.humidity, 0) / baseRooms.length : 0,
        energy: baseRooms.reduce((acc, r) => acc + r.energy, 0),
        aqi: baseRooms.length ? Math.round(baseRooms.reduce((acc, r) => acc + r.aqi, 0) / baseRooms.length) : 0,
        icon: LayoutGridIcon,
      };
      return [allRoom, ...baseRooms];
    }
    return baseRooms;
  }, [zones, assetTelemetry, allowedZoneIds, userRole, isTenantAdmin]);

  const fallbackRoom = useMemo(() => ({
    id: selectedRoomId || 'ALL',
    name: apartmentLabel || '전체 공간',
    temp: 0,
    humidity: 0,
    energy: 0,
    aqi: 0,
    co2: 450,
    tvoc: 0.1,
    dust: 10,
    icon: LayoutGridIcon,
  }), [selectedRoomId, apartmentLabel]);

  const currentRoom = ROOMS.find(r => r.id === selectedRoomId) || ROOMS[0] || fallbackRoom;

  // Handlers
  const [defaultSceneZoneId, setDefaultSceneZoneId] = useState<string>('ALL');

  const handleOpenSceneEditor = (scene: any | null, defaultZoneId?: string) => {
    setEditingScene(scene);
    if (defaultZoneId) {
      setDefaultSceneZoneId(defaultZoneId);
    } else if (selectedRoomId) {
      setDefaultSceneZoneId(selectedRoomId);
    }
    setSlideDirection('left');
    setCurrentView('sceneEditor');
  };

  const handleCloseSceneEditor = () => {
    setSlideDirection('right');
    setCurrentView('main');
    setEditingScene(null);
  };


  const handleSelectRoom = useCallback((roomId: string) => {
    setSelectedRoomId(roomId);
  }, []);

  const handleUpdateSensor = useCallback((updatedSensor: Sensor) => {
    setSelectedSensor(updatedSensor);
    if (updatedSensor.id && updatedSensor.label) {
      setSensorLabelOverrides(prev => ({
        ...prev,
        [updatedSensor.id]: updatedSensor.label
      }));
    }
  }, []);

  const handleSelectSensor = useCallback((sensor: Sensor) => {
    setSelectedSensor(sensor);
    setSlideDirection('left');
    setCurrentView('sensorDetail');
  }, []);

  const handleBackFromSensor = useCallback(() => {
    setSlideDirection('right');
    setCurrentView('main');
    setSelectedSensor(null);
  }, []);

  const handleOpenMap = useCallback(() => {
    if (!floorPlanUrl || floorPlanUrl.trim() === '') {
      handleShowToast('공간맵이 등록되어 있지 않습니다.', 'info');
      return;
    }
    setSlideDirection('left');
    setCurrentView('floorPlan');
  }, [floorPlanUrl, handleShowToast]);

  const handleCloseMap = useCallback(() => {
    setSlideDirection('right');
    setCurrentView('main');
  }, []);

  const handleTabChange = useCallback((tab: MainTabType) => {
    if (tab === 'menu') {
      setIsMenuOpen(true);
      return;
    }
    if (currentView !== 'main') {
      setSlideDirection('right');
      setCurrentView('main');
    }
    setMainTab(tab);
  }, [currentView]);

  // 1. Login & SignUp Screen (force if accessed via signup/customer QR link or not authenticated)
  if (!authToken || forceSignUpMode) {
    return (
      <LoginScreen
        onLogin={handleLogin}
        initialSignUpMode={forceSignUpMode}
      />
    );
  }

  // 2. Unit Selection Screen
  if (currentView === 'unitSelect' || !apartmentAssetId) {
    return (
      <UnitSelectionScreen
        isMobile
        onSelect={handleUnitSelect}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <div
      className="h-[100dvh] flex flex-col overflow-hidden max-w-md mx-auto relative font-sans"
      data-theme={resolvedTheme}
      style={{ backgroundColor: 'var(--mobile-bg)', color: 'var(--mobile-text-primary)' }}
    >
      {/* Background Decor */}
      <div
        className="fixed inset-0 pointer-events-none z-0 opacity-10 mobile-grid-decor"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '32px 32px'
        }}
      />
      {resolvedTheme === 'dark' && (
        <div className="fixed inset-0 pointer-events-none z-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a0f1a_100%)]" />
      )}

      {/* ─── Header Section ─── */}
      <div className="relative z-20 flex-shrink-0">
        {currentView === 'sensorDetail' && selectedSensor ? (
          <div
            className="h-14 flex items-center px-4 backdrop-blur-md border-b"
            style={{ backgroundColor: 'var(--mobile-bg-header)', borderColor: 'var(--mobile-border)' }}
          >
            <button onClick={handleBackFromSensor} aria-label="뒤로" className="p-2 -ml-1 rounded-lg transition-colors" style={{ color: 'var(--mobile-text-muted)' }}>
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex-1 text-center font-bold" style={{ color: 'var(--mobile-text-primary)' }}>
              {t('app.sed_info')}
            </div>
            <div className="w-9" />
          </div>
        ) : currentView === 'floorPlan' ? (
          <div
            className="h-14 flex items-center px-4 backdrop-blur-md border-b"
            style={{ backgroundColor: 'var(--mobile-bg-header)', borderColor: 'var(--mobile-border)' }}
          >
            <button onClick={handleCloseMap} aria-label="뒤로" className="p-2 -ml-1 rounded-lg transition-colors" style={{ color: 'var(--mobile-text-muted)' }}>
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div className="flex-1 text-center font-bold" style={{ color: 'var(--mobile-text-primary)' }}>
              {t('app.floorplan')}
            </div>
            <div className="w-9" />
          </div>
        ) : currentView === 'scenes' ? null : (
          <MobileHeader
            rooms={ROOMS as any}
            selectedRoomId={selectedRoomId || ''}
            onSelectRoom={handleSelectRoom}
            onOpenMap={handleOpenMap}
            onOpenAlarms={() => setShowAlarms(true)}
            onOpenScenes={() => {
              setSlideDirection('left');
              setCurrentView('scenes');
            }}
            hasAlarms={alarmMessages.length > 0}
            onBack={() => handleBackToUnits()}
            apartmentLabel={apartmentLabel || ''}
            currentRoomName={currentRoom?.name || ''}
            sensorsCount={currentSensors.length}
            themePref={themePref}
            onThemeToggle={toggleTheme}
            showZoneSelector={mainTab === 'home' || mainTab === 'devices'}
          />
        )}
      </div>

      {/* ─── Main Content Container ─── */}
      <div className="flex-1 flex flex-col relative z-10 overflow-hidden">
        <AnimatePresence initial={false}>
          {/* Main Dashboard / Zone Sensor View */}
          {currentView === 'main' && (
            <motion.div
              key="main"
              variants={slideVariants}
              initial={slideDirection === 'left' ? 'enterFromRight' : slideDirection === 'right' ? 'enterFromLeft' : undefined}
              animate="center"
              exit={slideDirection === 'right' ? 'exitToRight' : slideDirection === 'left' ? 'exitToLeft' : undefined}
              transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
              className="absolute inset-0 flex flex-col"
              style={{ backgroundColor: 'var(--mobile-bg-alt)' }}
            >
              {mainTab === 'home' ? (
                <AllSpacesView
                  apartmentAssetId={apartmentAssetId}
                  homeName={apartmentLabel}
                  seoSensors={seoSensors}
                  allSensors={allSensors}
                  selectedRoomId={selectedRoomId || 'ALL'}
                  rooms={ROOMS as any}
                  onSelectRoom={handleSelectRoom}
                  onSelectSensor={handleSelectSensor}
                  onShowToast={handleShowToast}
                  telemetry={assetTelemetry}
                  sensorTelemetry={sensorTelemetry}
                />
              ) : mainTab === 'devices' ? (
                <SensorListView
                  room={currentRoom as any}
                  telemetry={sensorTelemetry}
                  onSelectSensor={handleSelectSensor}
                  sensors={selectedRoomId === 'ALL' ? allSensors : currentSensors}
                  onShowToast={handleShowToast}
                />
              ) : (
                <SceneListView
                  apartmentAssetId={apartmentAssetId}
                  allSensors={allSensors}
                  rooms={ROOMS as any}
                  selectedRoomId={selectedRoomId || 'ALL'}
                  apartmentLabel={apartmentLabel || ''}
                  onShowToast={handleShowToast}
                  onOpenEditor={handleOpenSceneEditor}
                />
              )}
            </motion.div>
          )}

          {/* Sensor Detail (SED) View */}
          {currentView === 'sensorDetail' && selectedSensor && (
            <motion.div
              key="sensorDetail"
              variants={slideVariants}
              initial="enterFromRight"
              animate="center"
              exit="exitToRight"
              transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
              className="absolute inset-0 overflow-y-auto"
              style={{ backgroundColor: 'var(--mobile-bg-alt)' }}
            >
              <SensorDetailSidebar
                sensor={selectedSensor}
                onBack={handleBackFromSensor}
                onClose={handleBackFromSensor}
                onShowToast={handleShowToast}
                onUpdateSensor={handleUpdateSensor}
              />
            </motion.div>
          )}

          {/* Floor Plan View (B2B Key Map: No Senior Care, No Zoom-in, Click Zone -> Navigate to Zone View) */}
          {currentView === 'floorPlan' && (
            <motion.div
              key="floorPlan"
              variants={slideVariants}
              initial="enterFromRight"
              animate="center"
              exit="exitToRight"
              transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
              className="absolute inset-0 flex flex-col"
            >
              {/* View Mode Tabs */}
              <div className="px-5 mb-4 mt-2 flex-shrink-0">
                <div
                  className="flex backdrop-blur-md rounded-2xl p-1 border shadow-inner"
                  style={{ background: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border)' }}
                >
                  {VIEW_MODES.map((mode) => {
                    const Icon = mode.icon;
                    const isActive =
                      viewMode === mode.id ||
                      (viewMode === 'aqi' && mode.id === 'airquality') ||
                      (viewMode === 'airquality' && mode.id === 'aqi');
                    return (
                      <button
                        key={mode.id}
                        onClick={() => setViewMode(mode.id)}
                        className={`flex-1 flex flex-col items-center justify-center py-2.5 rounded-xl transition-all duration-300 relative`}
                        style={
                          isActive
                            ? { background: 'var(--mobile-tab-active)', color: 'var(--mobile-text-primary)' }
                            : { color: 'var(--mobile-text-muted)' }
                        }
                      >
                        <Icon className={`w-5 h-5 mb-1 ${isActive ? mode.activeColor : 'text-[var(--mobile-text-muted)]'}`} />
                        <span className={`text-[0.8rem] font-medium font-bold uppercase tracking-wider ${isActive ? mode.activeColor : 'text-[var(--mobile-text-muted)]'}`}>
                          {t(mode.label)}
                        </span>
                        {isActive && (
                          <motion.div
                            layoutId="activeTab"
                            className="absolute -bottom-1 w-1 h-1 rounded-full bg-cyan-400"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Floor Plan Heat Map */}
              <div className="flex-1 flex items-center justify-center px-5 py-3 overflow-hidden">
                <FloorPlanHeatMap
                  rooms={ROOMS as any}
                  selectedRoomId={selectedRoomId || ''}
                  onSelectRoom={(id) => {
                    handleSelectRoom(id);
                    // Click zone -> Go straight to zone devices list
                    setMainTab('devices');
                    setSlideDirection('right');
                    setCurrentView('main');
                  }}
                  onSelectSensor={handleSelectSensor}
                  viewMode={viewMode}
                  zoneOverrides={zones.map((z) => ({
                    id: z.id,
                    name: z.name,
                    top: z.top,
                    left: z.left,
                    width: z.width,
                    height: z.height,
                    points: z.points,
                    sensors: z.sensors
                  }))}
                  floorPlanUrl={floorPlanUrl}
                  currentAssetId={apartmentAssetId}
                  allSensors={allSensors}
                  sensorTelemetry={sensorTelemetry}
                />
              </div>
            </motion.div>
          )}

          {/* Smart Scene / Automation View */}
          {currentView === 'scenes' && (
            <motion.div
              key="scenes"
              variants={slideVariants}
              initial="enterFromRight"
              animate="center"
              exit="exitToRight"
              transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
              className="absolute inset-0 flex flex-col"
            >
              <SceneListView
                apartmentAssetId={apartmentAssetId}
                allSensors={allSensors}
                onShowToast={handleShowToast}
                onBack={() => {
                  setSlideDirection('right');
                  setCurrentView('main');
                  setMainTab('home');
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

            {/* ─── Bottom Navigation ─── */}
      {currentView === 'main' && (
        <BottomNavigation
          activeTab={mainTab}
          onChangeTab={handleTabChange}
          hasAlarms={alarmMessages.length > 0}
        />
      )}

      {/* ─── Menu Sheet Drawer ─── */}
      <MenuSheet
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        apartmentLabel={apartmentLabel || ''}
        onSelectUnit={handleBackToUnits}
        onOpenMap={handleOpenMap}
        onOpenAlarms={() => setShowAlarms(true)}
        hasAlarms={alarmMessages.length > 0}
        isAdmin={isAdmin}
        onOpenUserManagement={() => setIsUserMgmtOpen(true)}
        themePref={themePref}
        onThemeToggle={toggleTheme}
        onLogout={handleLogout}
      />

      {/* ─── User & Permission Management Modal (Admin Only) ─── */}
      <UserManagementModal
        isOpen={isUserMgmtOpen}
        onClose={() => setIsUserMgmtOpen(false)}
        customerId={currentSpaceCustomerId || currentUser?.customerId?.id || ''}
        spaceName={apartmentLabel || '202호'}
        zones={allZones}
      />

      {/* ─── Alarms Sheet Overlay ─── */}
      <AnimatePresence>
        {showAlarms && (
          <AlarmsSheet
            onClose={() => setShowAlarms(false)}
            apartmentAssetId={apartmentAssetId}
          />
        )}
      </AnimatePresence>

                {/* Scene Editor View (Fullscreen Modal) */}
          {currentView === 'sceneEditor' && (
            <motion.div
              key="sceneEditor"
              variants={slideVariants}
              initial="enterFromRight"
              animate="center"
              exit="exitToRight"
              transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
              className="absolute inset-0 flex flex-col z-50"
            >
              <SceneEditorModal
                isOpen={true}
                scene={editingScene}
                allSensors={allSensors}
                rooms={ROOMS as any}
                defaultRoomId={defaultSceneZoneId || selectedRoomId || 'ALL'}
                apartmentLabel={apartmentLabel || ''}
                onClose={handleCloseSceneEditor}
                onSave={async (savedScene) => {
                  if (apartmentAssetId) {
                    const res = await sceneService.saveScene(savedScene, apartmentAssetId);
                    handleShowToast(res.message, res.success ? 'success' : 'error');
                  }
                  handleCloseSceneEditor();
                }}
              />
            </motion.div>
          )}

      {/* ─── Curtain / Blind Controller ─── */}
      <CurtainController
        isOpen={!!deviceToast && deviceToast.name.startsWith('SEC_')}
        deviceName={deviceToast?.name || null}
        alias={deviceToast?.alias || null}
        onClose={() => setDeviceToast(null)}
        sensors={allSensors}
      />

      {/* ─── Notification Toast ─── */}
      <AnimatePresence>
        {appToast && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[400] px-4 py-2.5 rounded-xl backdrop-blur-xl border shadow-2xl flex items-center gap-2 text-xs font-bold"
            style={{
              backgroundColor:
                appToast.type === 'success'
                  ? 'rgba(16, 185, 129, 0.9)'
                  : appToast.type === 'error'
                  ? 'rgba(239, 68, 68, 0.9)'
                  : 'rgba(6, 182, 212, 0.9)',
              color: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.2)'
            }}
          >
            {appToast.type === 'success' && <CheckCircle2Icon className="w-4 h-4" />}
            {appToast.type === 'error' && <AlertCircleIcon className="w-4 h-4" />}
            {appToast.type === 'info' && <InfoIcon className="w-4 h-4" />}
            <span>{appToast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default B2BMobileApp;
